import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, CreditCard, DiscordLogo } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import FeedbackForm from "@/components/FeedbackForm";
import { toast } from "sonner";
import { formatGP, STATUS_COLOR } from "@/lib/format";

const STEPS = ["pending", "delivered"];

export default function OrderView({ o, payment }) {
  const idx = o.status === "delivered" ? 1 : 0;
  const { data: site } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [paying, setPaying] = useState(false);
  const [fbDone, setFbDone] = useState(false);
  const usd = (o.total * (site?.usd_per_million_gp || 0));
  const { data: dinfo } = useQuery({ queryKey: ["discord-info"], queryFn: () => api.get("/discord/info").then((r) => r.data) });
  useEffect(() => {
    const key = `bomber_autoconnect_${o.code}`;
    if (dinfo?.oauth && !o.discord_id && o.status !== "cancelled" && sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
      connectDiscord();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dinfo?.oauth, o.code]);
  const names = o.items.map((i) => i.title).join(", ");
  const chatUrl = o.discord_id && o.discord_channel_id && dinfo?.guild_id ? `https://discord.com/channels/${dinfo.guild_id}/${o.discord_channel_id}` : "";
  const msg = `I bought ${names} - order ${o.code}`;
  const connectDiscord = async () => {
    try {
      const { data } = await api.post("/discord/link", { code: o.code, email: o.email });
      window.location.href = data.url;
    } catch (err) { toast.error(errMsg(err)); }
  };
  const openTicketChannel = () => { navigator.clipboard?.writeText(msg); toast.success("Message copied. Paste it in the ticket."); window.open(dinfo.ticket_url || "https://discord.com/app", "_blank", "noopener"); };
  const payCard = async () => {
    setPaying(true);
    try {
      sessionStorage.setItem("bomber_pay_email", o.email);
      const { data } = await api.post("/payments/checkout", { code: o.code, email: o.email, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setPaying(false); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  return (
    <div className="border border-[#ff2a55]/40 bg-[#090909]" data-testid="order-view">
      <div className="p-6 md:p-8 flex flex-wrap items-center justify-between gap-4 border-b border-white/10">
        <div>
          <div className="overline-label">Order ticket</div>
          <div className="font-display text-5xl chrome-text leading-none flex items-center gap-3" data-testid="order-code">{o.code}
            <button onClick={() => copy(o.code)} className="text-[#777] hover:text-white transition-colors" aria-label="Copy code"><Copy size={22} /></button>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#777]">Status</div>
          <div className="font-display text-3xl uppercase" style={{ color: STATUS_COLOR[o.status] }} data-testid="order-status">{o.status}</div>
        </div>
      </div>
      {o.status !== "cancelled" && (
        <div className="px-6 md:px-8 py-6 grid grid-cols-2 gap-2">
          {STEPS.map((s, i) => (
            <div key={s}>
              <div className="h-1" style={{ background: i <= idx ? "#ff2a55" : "#222", boxShadow: i <= idx ? "0 0 12px #ff2a55" : "none" }} />
              <div className={`text-[10px] tracking-[0.2em] uppercase mt-2 ${i <= idx ? "text-white" : "text-[#555]"}`}>{s}</div>
            </div>
          ))}
        </div>
      )}
      <div className="px-6 md:px-8 pb-6 space-y-2">
        {o.items.map((it, i) => (
          <div key={i} className="border border-white/10 p-4" data-testid={`order-item-${i}`}>
            <div className="flex justify-between items-center">
              <span className="font-display text-2xl chrome-text-sm">{it.title}</span>
              <span className="font-display text-xl hot-text">{formatGP(it.price)}</span>
            </div>
            {it.delivery_details && (
              <div className="mt-3 bg-[#0f1a12] border border-[#35d07f]/50 p-4" data-testid={`order-delivery-${i}`}>
                <div className="overline-label !text-[#35d07f] mb-2">Secure RSN transfer pack</div>
                <p className="mb-3 text-xs text-[#9bc9a8]">Payment confirmed. Use these details to complete your name transfer, then store them somewhere safe.</p>
                <pre className="whitespace-pre-wrap font-mono text-sm text-white">{it.delivery_details}</pre>
                <button className="mt-3 text-xs tracking-[0.2em] uppercase text-[#35d07f] hover:text-white transition-colors" onClick={() => copy(it.delivery_details)}>Copy transfer details</button>
              </div>
            )}
          </div>
        ))}
        <div className="flex justify-between pt-3"><span className="overline-label">Total</span><span className="font-display text-3xl hot-text">{formatGP(o.total)} GP</span></div>
      </div>
      {o.status === "delivered" && !o.feedback_left && !fbDone && <FeedbackForm order={o} onDone={() => setFbDone(true)} />}
      {o.status === "delivered" && (o.feedback_left || fbDone) && <div className="m-6 md:m-8 mb-0 text-xs tracking-widest uppercase text-[#35d07f]" data-testid="feedback-thanks">Thanks for your feedback</div>}
      {dinfo?.enabled && o.status !== "cancelled" && (
        <div className="m-6 md:m-8 mb-0 p-4 border border-[#5865f2]/60 bg-[#0b0d1f] flex flex-wrap items-center justify-between gap-3" data-testid="order-discord-panel">
          <div>
            <div className="overline-label !text-[#8c96ff]">Talk to the seller</div>
            <div className="text-sm text-[#ddd]">
              {chatUrl ? "Your private order ticket is ready on Discord." : o.discord_id ? "Adding you to your private order ticket..." : dinfo.oauth ? "Your order ticket is open. Connect Discord to join it and chat with a moderator." : "Open our Discord ticket channel and paste your order message."}
            </div>
          </div>
          {chatUrl ? <a className="btn-bomb !py-2 inline-flex items-center gap-2" href={chatUrl} target="_blank" rel="noreferrer" data-testid="order-discord-open-button"><DiscordLogo size={18} weight="fill" /> Open Discord chat</a>
            : o.discord_id ? <span className="text-xs tracking-widest uppercase text-[#8c96ff] animate-flicker" data-testid="order-discord-pending">Linking...</span>
            : dinfo.oauth ? <button className="btn-bomb !py-2 inline-flex items-center gap-2" onClick={connectDiscord} data-testid="order-discord-connect-button"><DiscordLogo size={18} weight="fill" /> Connect Discord and chat</button>
            : <button className="btn-bomb !py-2 inline-flex items-center gap-2" onClick={openTicketChannel} data-testid="order-discord-ticket-button"><DiscordLogo size={18} weight="fill" /> Open Discord and copy message</button>}
        </div>
      )}
      {o.status === "pending" && usd > 0 && (
        <div className="m-6 md:m-8 mb-0 p-4 border border-[#ff2a55]/60 bg-[#12060a] flex flex-wrap items-center justify-between gap-3" data-testid="order-card-payment">
          <div><div className="overline-label !text-[#ff2a55]">Instant delivery</div><div className="text-sm text-[#ddd]">Pay by card (${Math.max(0.5, usd).toFixed(2)} USD). Your transfer pack unlocks the moment payment clears.</div></div>
          <button className="btn-bomb !py-2" onClick={payCard} disabled={paying} data-testid="order-pay-card-button"><CreditCard size={18} /> {paying ? "Redirecting..." : "Pay with card"}</button>
        </div>
      )}
      {payment && o.status === "pending" && (
        <div className="m-6 md:m-8 mt-0 p-4 border border-[#ffb020]/50 bg-[#1a1405] text-sm text-[#ddd]" data-testid="order-payment-instructions">
          <div className="overline-label !text-[#ffb020] mb-1">Complete payment</div>{payment}
          <p className="mt-2 text-xs text-[#aa9878]">Keep this page open. Your secure RSN transfer pack appears here automatically after payment is confirmed.</p>
        </div>
      )}
    </div>
  );
}
